import { Directive, ElementRef, Input, OnChanges, OnDestroy, OnInit, Renderer2, SimpleChanges } from '@angular/core';

/** 保留 uuid */
function uuidv4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c == 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

let STYLE_INJECTED = false; // 全域樣式只注入一次

@Directive({
  selector: '[appLoading]',
})
export class LoadingDirective implements OnInit, OnChanges, OnDestroy {
  @Input() appLoading: boolean = false;
  @Input() loadingBgColor: string = 'transparent';
  @Input() intervalMs = 900; // 每張停留時間（需 > transitionMs）

  uid?: string;

  private readonly transitionMs = 360; // 單次滑動動畫時間
  private images: string[] = [
    '/assets/images/loading/01.png',
    '/assets/images/loading/02.png',
    '/assets/images/loading/03.png',
    '/assets/images/loading/04.png',
    '/assets/images/loading/05.png',
  ];

  // 槽位：0 FarLeft | 1 Left | 2 Center | 3 Right | 4 FarRight
  private wrappers: HTMLElement[] = [];
  private imgs: HTMLImageElement[] = [];

  private leftIndex = 0; // Left 位置對應的圖片索引
  private animating = false;
  private timer?: any;

  // DOM 參考
  private loadingContainer!: HTMLElement;
  private stage!: HTMLElement;

  // 立體位置（Far 兩側拉更遠避免初載露邊）
  private pos = [
    'translate3d(-220px,0,0) translateZ(-120px) rotateY(22deg) scale(0.82)', // FarLeft（隱）
    'translate3d(-80px,0,0)  translateZ(-60px)  rotateY(14deg) scale(0.90)', // Left（模糊）
    'translate3d(0,0,0)     translateZ(0px)    rotateY(0deg)  scale(1.03)', // Center（清楚）
    'translate3d(80px,0,0)  translateZ(-60px)  rotateY(-14deg) scale(0.90)', // Right（模糊）
    'translate3d(220px,0,0) translateZ(-120px) rotateY(-22deg) scale(0.82)', // FarRight（隱）
  ] as const;

  constructor(
    private targetEl: ElementRef<HTMLElement>,
    private renderer: Renderer2,
  ) {}

  // 只在第一次使用時注入一段全域樣式
  private injectGlobalStyleOnce() {
    if (STYLE_INJECTED) return;
    STYLE_INJECTED = true;
    const css = `
/* 當 host 有 .app-loading-hide 時，隱藏除了 .app-local-loading 以外的「直屬」子節點 */
.app-loading-hide > :not(.app-local-loading) { display: none !important; }
`;
    const styleEl = this.renderer.createElement('style');
    this.renderer.setProperty(styleEl, 'type', 'text/css');
    this.renderer.appendChild(styleEl, this.renderer.createText(css));
    (document.head || document.body).appendChild(styleEl);
  }

  ngOnInit() {
    this.injectGlobalStyleOnce();

    const host = this.targetEl.nativeElement;
    this.renderer.setStyle(host, 'min-height', '120px');

    if (getComputedStyle(host).getPropertyValue('position') === 'static') {
      host.style.position = 'relative';
    }

    // 少於 3 張補滿，避免空位
    if (this.images.length < 3) {
      const need = 3 - this.images.length;
      for (let i = 0; i < need; i++) this.images.push(this.images[i % this.images.length]);
    }

    this.uid = 'loading-container-' + uuidv4();

    // 外層覆蓋層（先隱形，避免初載露邊）
    this.loadingContainer = this.renderer.createElement('div');
    this.renderer.addClass(this.loadingContainer, 'app-local-loading');
    this.renderer.addClass(this.loadingContainer, this.uid!);
    this.renderer.setStyle(this.loadingContainer, 'display', this.appLoading ? 'flex' : 'none');
    this.renderer.setStyle(this.loadingContainer, 'background-color', this.loadingBgColor);
    this.renderer.setStyle(this.loadingContainer, 'opacity', '0');
    this.renderer.setStyle(this.loadingContainer, 'visibility', 'hidden');
    this.renderer.setStyle(this.loadingContainer, 'transition', 'opacity 150ms ease');

    // 覆蓋層鋪滿 host
    this.renderer.setStyle(this.loadingContainer, 'position', 'absolute');
    this.renderer.setStyle(this.loadingContainer, 'inset', '0');
    this.renderer.setStyle(this.loadingContainer, 'align-items', 'center');
    this.renderer.setStyle(this.loadingContainer, 'justify-content', 'center');

    // 舞台（加入 perspective）
    this.stage = this.renderer.createElement('div');
    this.renderer.addClass(this.stage, 'app-local-loading-spinner');
    this.renderer.setStyle(this.stage, 'position', 'relative');
    this.renderer.setStyle(this.stage, 'width', '240px');
    this.renderer.setStyle(this.stage, 'height', '80px');
    this.renderer.setStyle(this.stage, 'display', 'block');
    this.renderer.setStyle(this.stage, 'overflow', 'hidden');
    this.renderer.setStyle(this.stage, 'perspective', '600px');
    this.renderer.setStyle(this.stage, 'perspective-origin', '50% 50%');

    // 建立 5 槽位
    for (let i = 0; i < 5; i++) {
      const wrap = this.renderer.createElement('div') as HTMLElement;
      const img = this.renderer.createElement('img') as HTMLImageElement;

      // wrapper：3D + 平滑位移
      this.renderer.setStyle(wrap, 'position', 'absolute');
      this.renderer.setStyle(wrap, 'left', '50%');
      this.renderer.setStyle(wrap, 'top', '50%');
      this.renderer.setStyle(wrap, 'width', '64px');
      this.renderer.setStyle(wrap, 'height', '64px');
      this.renderer.setStyle(wrap, 'margin', '-32px 0 0 -32px');
      this.renderer.setStyle(wrap, 'transform-style', 'preserve-3d');
      this.renderer.setStyle(wrap, 'transition', `transform ${this.transitionMs}ms cubic-bezier(0.22, 0.61, 0.36, 1)`);
      this.renderer.setStyle(wrap, 'will-change', 'transform');
      this.renderer.setStyle(wrap, 'pointer-events', 'none');

      // img：避免背面閃爍；用 blur/opacity 做層次
      this.renderer.setStyle(img, 'width', '100%');
      this.renderer.setStyle(img, 'height', '100%');
      this.renderer.setStyle(img, 'object-fit', 'contain');
      this.renderer.setStyle(img, 'backface-visibility', 'hidden');
      this.renderer.setStyle(
        img,
        'transition',
        `filter ${this.transitionMs - 60}ms ease, opacity ${this.transitionMs - 60}ms ease`,
      );
      this.renderer.setStyle(img, 'will-change', 'filter, opacity');

      this.renderer.appendChild(wrap, img);
      this.renderer.appendChild(this.stage, wrap);
      this.wrappers.push(wrap);
      this.imgs.push(img);
    }

    this.renderer.appendChild(this.loadingContainer, this.stage);
    this.renderer.appendChild(host, this.loadingContainer);

    // 初始佈局（先隱形瞬移定位，再顯示）
    this.leftIndex = 0;
    this.applyLayout(true);
    this.revealAfterLayout();

    // 依初始 appLoading 狀態：用 class 控制顯示/隱藏子層（除了 .app-local-loading）
    if (this.appLoading) {
      host.classList.add('app-loading-hide');
      this.start();
    } else {
      host.classList.remove('app-loading-hide');
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.appLoading) {
      const host = this.targetEl.nativeElement;
      const div = this.loadingContainer ?? (host.querySelector('.' + this.uid) as HTMLElement | null);
      if (div) this.renderer.setStyle(div, 'display', this.appLoading ? 'flex' : 'none');

      // 用 class 控制隱藏或顯示子層（除了 .app-local-loading）
      if (this.appLoading) {
        host.classList.add('app-loading-hide');
        this.start();
      } else {
        host.classList.remove('app-loading-hide');
        this.stop();
      }
    }
    if (changes.loadingBgColor && this.loadingContainer) {
      this.renderer.setStyle(this.loadingContainer, 'background-color', this.loadingBgColor);
    }
    if (changes.intervalMs && this.appLoading) {
      this.start(); // 間隔變更時重啟
    }
  }

  ngOnDestroy(): void {
    this.stop();
  }

  // === 佈局（不觸發動畫）===
  private applyLayout(initial = false) {
    const N = this.images.length;
    const idx = [
      this.leftIndex - 1, // FarLeft
      this.leftIndex, // Left
      this.leftIndex + 1, // Center
      this.leftIndex + 2, // Right
      this.leftIndex + 3, // FarRight
    ].map(k => ((k % N) + N) % N);

    for (let slot = 0; slot < 5; slot++) {
      const wrap = this.wrappers[slot];
      const img = this.imgs[slot];
      if (!wrap || !img) continue;

      this.withoutTransition(wrap, () => (wrap.style.transform = this.pos[slot]));

      if (initial || img.dataset._k !== String(idx[slot])) {
        img.src = this.images[idx[slot]];
        img.dataset._k = String(idx[slot]);
      }

      // 視覺層次
      if (slot === 2) {
        wrap.style.zIndex = '3';
        img.style.opacity = '1';
        img.style.filter = 'drop-shadow(0 2px 6px rgba(0,0,0,.25))';
      } else if (slot === 1 || slot === 3) {
        wrap.style.zIndex = '2';
        img.style.opacity = '0.7';
        img.style.filter = 'blur(2px)';
      } else {
        wrap.style.zIndex = '1';
        img.style.opacity = '0';
        img.style.filter = 'blur(2px)';
      }
    }
  }

  // === 啟動／停止 ===
  private start() {
    this.stop();

    // 顯示容器 & 先隱形，重新瞬移定位避免露邊
    if (this.loadingContainer) {
      this.loadingContainer.style.display = 'flex';
      this.loadingContainer.style.opacity = '0';
      this.loadingContainer.style.visibility = 'hidden';
    }
    this.applyLayout(true);
    this.revealAfterLayout();

    // 確保 interval > transition
    if (this.intervalMs < this.transitionMs + 40) this.intervalMs = this.transitionMs + 40;

    // 右 -> 左
    this.timer = setInterval(() => this.stepLeft(), this.intervalMs);
  }

  private stop() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = undefined;
    }
    this.animating = false;
  }

  // === 左移：左邊滑出、右邊新進（無限循環 + 立體感）===
  private stepLeft() {
    if (this.animating) return;
    this.animating = true;

    const [wFL, wL, wC, wR, wFR] = this.wrappers;
    const [iFL, iL, iC, iR, iFR] = this.imgs;

    // 暫時隱 wFL，動畫後瞬移到 FarRight
    wFL.style.display = 'none';

    // 視覺過渡（L -> FL 隱形、C -> L 模糊、R -> C 清晰、FR -> R 模糊）
    iL.style.filter = 'blur(2px)';
    iL.style.opacity = '0'; // L -> FL
    iC.style.filter = 'blur(2px)';
    iC.style.opacity = '0.7'; // C -> L
    iR.style.filter = 'drop-shadow(0 2px 6px rgba(0,0,0,.25))';
    iR.style.opacity = '1'; // R -> C
    iFR.style.filter = 'blur(2px)';
    iFR.style.opacity = '0.7'; // FR -> R

    // 位置位移（觸發 transition）
    wL.style.transform = this.pos[0]; // 1 -> 0
    wC.style.transform = this.pos[1]; // 2 -> 1
    wR.style.transform = this.pos[2]; // 3 -> 2
    wFR.style.transform = this.pos[3]; // 4 -> 3
    // wFL 保持在 pos[0]，動畫後瞬移到 pos[4]

    const finish = () => {
      const N = this.images.length;

      // 邏輯往左一格：Left 對應圖片索引往「下一張」
      this.leftIndex = (this.leftIndex + 1) % N;

      // 新的 FarRight（下一張要從右邊進場）
      const newFarRightIdx = (this.leftIndex + 3) % N;

      // FarLeft 瞬移到 FarRight 並換圖
      this.withoutTransition(wFL, () => {
        wFL.style.transform = this.pos[4];
      });
      iFL.src = this.images[newFarRightIdx];
      iFL.dataset._k = String(newFarRightIdx);
      wFL.style.display = '';

      // 槽位左旋，確保下一輪仍會動
      // [FL, L, C, R, FR] -> [L, C, R, FR, FL]
      this.wrappers = [wL, wC, wR, wFR, wFL];
      this.imgs = [iL, iC, iR, iFR, iFL];

      this.animating = false;
    };

    // 監聽「移到 Center」的元素（wR）的 transform 動畫結束
    const off = this.renderer.listen(wR, 'transitionend', (ev: TransitionEvent) => {
      if (ev.propertyName !== 'transform') return;
      off();
      finish();
    });

    // 安全網：避免 transitionend 遺失
    setTimeout(() => {
      if (!this.animating) return;
      off();
      finish();
    }, this.transitionMs + 90);
  }

  // 工具：臨時移除 transition 做瞬移（不閃爍）
  private withoutTransition(el: HTMLElement, fn: () => void) {
    if (!el) return;
    const old = el.style.transition;
    el.style.transition = 'none';
    fn();
    // 強制 reflow
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    el.offsetHeight;
    el.style.transition = old || `transform ${this.transitionMs}ms cubic-bezier(0.22, 0.61, 0.36, 1)`;
  }

  // 工具：兩次 rAF 後顯示，確保初始 transform 已就位
  private revealAfterLayout() {
    if (!this.loadingContainer) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.loadingContainer.style.visibility = 'visible';
        this.loadingContainer.style.opacity = '1';
      });
    });
  }
}
